import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";

import type { RephraseQuestionType } from "@performyard/service-contracts";

import { AIButton } from "../../../../components/AIModules/AIButton/AIButton";

import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";

type AIAssistantPortalProps = {
	elementId: string;
};

const findTextAreas = (elementId: string) => {
	// Find the question block container
	const anchorElement = document.getElementById(`anchor-ai-assistant-${elementId}`);
	const questionBlock = anchorElement?.closest(".question-block");

	if (questionBlock) {
		// Find all textareas within this question block
		return questionBlock.querySelectorAll("textarea");
	}

	// Fallback to ID-based search
	return document.querySelectorAll(`textarea[id^="textarea-${elementId}"]`);
};
// Create array of values from text areas, remove any new lines within a single answer
const collectTextValues = (textAreas: NodeListOf<Element>) => {
	return Array.from(textAreas).map((textArea) => (textArea as HTMLTextAreaElement).value || "");
};

/**
 * Sets textarea value and dispatches native input/change events
 * to ensure all event listeners (including React state updates) are triggered
 */
const setTextareaValue = (textarea: HTMLTextAreaElement, value: string): void => {
	textarea.value = value;

	// Dispatch input event (bubbles up to trigger event listeners)
	const inputEvent = new Event("input", { bubbles: true });
	textarea.dispatchEvent(inputEvent);

	// Dispatch change event for completeness
	const changeEvent = new Event("change", { bubbles: true });
	textarea.dispatchEvent(changeEvent);
};

/**
 * AIAssistantPortal component
 *
 * Given the elementId, it will find the anchor element and the text areas.
 * The button will attach to the anchor element.
 * A listener is added to each text area to capture when the user is done editing.
 * This component tracks textarea values in state and listens for input events
 * to keep the button's disabled state in sync with the current textarea content.
 * This component also observes DOM changes to re-attach listeners when textareas are added/removed.
 * Since we allows adding new lines by clicking enter, the textarea is added to the DOM but the state is not updated.
 * So we need to observe DOM changes to re-attach listeners when textareas are added/removed.
 */
const AIAssistantPortal = ({ elementId }: AIAssistantPortalProps) => {
	const aiEnabled = useFeatureFlag("ff-is-ai-enabled", false);
	const rootElement = document.getElementById(`anchor-ai-assistant-${elementId}`);

	const question = rootElement?.getAttribute("data-question");
	const subText = rootElement?.getAttribute("data-subtext");
	const subject = rootElement?.getAttribute("data-subject");
	const author = rootElement?.getAttribute("data-author");
	const goalName = rootElement?.getAttribute("data-goal-name") || "";
	const questionType = rootElement?.getAttribute("data-type") as RephraseQuestionType;

	const textareas = findTextAreas(elementId);
	const [answers, setAnswers] = useState<string[]>(() => collectTextValues(textareas));

	// Parse context from textarea
	let context: Record<string, unknown> = {};
	try {
		context = JSON.parse(textareas[0]?.getAttribute("data-context") || "{}");
	} catch (e) {
		console.error("Error parsing context", e);
	}

	// Listen for input events on textareas to update state
	// Also observe DOM changes to handle dynamically added/removed textareas (multiline questions)
	useEffect(() => {
		const handleInput = () => {
			const currentTextAreas = findTextAreas(elementId);
			setAnswers(collectTextValues(currentTextAreas));
		};

		const attachListeners = () => {
			const currentTextAreas = findTextAreas(elementId);
			currentTextAreas.forEach((textarea) => {
				textarea.addEventListener("input", handleInput);
			});
			return currentTextAreas;
		};

		const detachListeners = (textareas: NodeListOf<Element>) => {
			textareas.forEach((textarea) => {
				textarea.removeEventListener("input", handleInput);
			});
		};

		// Initial attachment
		let currentTextAreas = attachListeners();

		// Observe DOM changes to re-attach listeners when textareas are added/removed
		// Since when user adds a new textarea by clicking enter, the textarea is added to the DOM but the state is not updated.
		const anchorElement = document.getElementById(`anchor-ai-assistant-${elementId}`);
		const questionBlock = anchorElement?.closest(".question-block");

		if (questionBlock) {
			const observer = new MutationObserver(() => {
				// Detach old listeners
				detachListeners(currentTextAreas);
				// Reattach to current textareas
				currentTextAreas = attachListeners();
				// Update state with current values
				handleInput();
			});

			observer.observe(questionBlock, {
				childList: true,
				subtree: true,
			});

			return () => {
				observer.disconnect();
				detachListeners(currentTextAreas);
			};
		}

		return () => {
			detachListeners(currentTextAreas);
		};
	}, [elementId]);

	if (!aiEnabled) {
		return null;
	}

	const content = (
		<div
			style={{
				width: "100%",
				display: "flex",
				justifyContent: "end",
				alignItems: "center",
				paddingTop: 4,
			}}
		>
			<AIButton
				moduleType="rephrase"
				disabled={answers.length === 0 || answers.every((answer) => !answer.trim())}
				question={question || ""}
				subText={subText || ""}
				subject={subject || ""}
				author={author || ""}
				answer={answers}
				questionType={questionType}
				goalName={goalName}
				context={context}
				onInsert={(newAnswer: string | string[]) => {
					// Query for textareas dynamically to include any newly added ones
					const currentTextAreas = findTextAreas(elementId);

					if (questionType === "multiline") {
						const lines = Array.isArray(newAnswer) ? newAnswer : newAnswer.split("\n");
						lines.forEach((line, index) => {
							const textarea = currentTextAreas[index] as HTMLTextAreaElement;
							if (textarea) {
								setTextareaValue(textarea, line);
							}
						});
					} else {
						const textarea = currentTextAreas[0] as HTMLTextAreaElement;
						if (textarea) {
							const value = Array.isArray(newAnswer) ? newAnswer.join("\n") : newAnswer;
							setTextareaValue(textarea, value);
						}
					}

					// Update state after inserting
					setAnswers(collectTextValues(currentTextAreas));
				}}
			/>
		</div>
	);

	if (!rootElement) {
		return null;
	}

	//attach portal to root element
	return ReactDOM.createPortal(content, rootElement as Element);
};

export default AIAssistantPortal;
